<template>
  <section id="flow" v-if="userPrefsInitialized">
    <vue-headful
      :title="$t('TITLE_TYPE_NAME', { type: $tc('FLOW', 1), name: this.flow.name }) | formatTitle"
    ></vue-headful>
    <application-menu
      :app="app"
      :flowName="flow.name"
      :flowGroups="savedGroups"
      :mode="editorMode"
    />
    <!--
      the '.ui.form' parent breaks stuff in blockly and should only be relevant in the settings tab
    -->
    <div class="ui top attached basic segment" id="editor" :class="{ dark: isDarkBorderRequired }">
      <form
        :class="{
          'ui info success warning error form':
            editorMode === 'settings' || editorMode === 'extensions'
        }"
        @click="$v.$touch()"
        autocomplete="off"
      >
        <div
          class="ui top attached red customFloating label"
          style="opacity: 0.8;"
          v-if="isCurrentFlowVersionOutdated"
        >
          {{ $t('WARN_OUTDATED', { thing: $tc('FLOW', 1) }) }}
        </div>
        <div
          class="ui top attached red customFloating label"
          style="opacity: 0.8;"
          v-if="!isCurrentUserAllowedToEditFlow"
        >
          {{ $t('FLOW_EDIT_NOT_FOR_THIS_MAINTAINER') }}
        </div>

        <!-- CODE -->
        <code-editor
          class="codeEditor"
          ref="codeEditor"
          v-show="editorMode === 'code'"
          :active="editorMode === 'code'"
          :flowId="flowId"
          @codeParsed="codeParsed"
        />

        <!-- BLOCKLY -->
        <div
          v-if="editorMode === 'blockly' && !blocklyEnabled"
          class="ui middle aligned centered padded grid"
          style="height: 100%"
        >
          <div class="row">
            <div class="ten wide column">
              <div class="ui attached message">
                <div class="header">{{ $t('BLOCKLY_EMPTY_HEADER') }}</div>
                {{ $t('BLOCKLY_EMPTY_MESSAGE') }}
              </div>
              <form class="ui form attached fluid segment" style="text-align: center;">
                <div class="negative ui button" @click="generateBlocksFromCode">
                  {{ $t('BLOCKLY_GENERATE_BUTTON') }}
                </div>
              </form>
              <div class="ui bottom attached warning message">
                <i class="icon exclamation triangle"></i>
                {{ $t('BLOCKLY_EMPTY_WARN') }}
              </div>
            </div>
          </div>
        </div>
        <div id="blockly" v-if="editorMode === 'blockly' && blocklyEnabled">
          <blockly-editor :app-id="appId" :flow-id="flow.id" @code-changed="blocklyUpdated" />
        </div>

        <!-- SETTINGS -->
        <flow-settings
          v-if="editorMode === 'settings'"
          :flowUser="flowUser"
          :copied="this.modeArg === 'copy'"
          :allGroups="groups"
          :savedGroups="savedGroups"
          :editedGroups="editedGroups"
          :groupSelected="groupSelected"
          :groupCreated="groupCreated"
          :groupRemoved="groupRemoved"
        />

        <!-- EXTENSIONS -->
        <flow-extensions v-if="editorMode === 'extensions'" @validates="extensionValidates" />

        <!-- CHANGELOG -->
        <changelog
          v-if="editorMode === 'changelog'"
          @flowReverted="onFlowReverted"
          class="changelog"
        />

        <!-- DEBUGGER -->
        <debugger v-if="editorMode === 'debugger' && isDebuggerEnabled" />

        <!-- HUB PUBLICATION DETAILS -->
        <flow-hub v-if="editorMode === 'hub'" :flow="flow" />
      </form>
    </div>
    <flow-button-menu
      :deleteHelp="
        !canDelete ? $t('FLOW_CANNOT_DELETE_LAUNCH_HELP') : $t('DELETE', { thing: $tc('FLOW', 1) })
      "
      :canDelete="canDelete"
      :deleteFlow="deleteFlow"
      :updateFlow="updateFlow"
      :copyFlow="copyFlow"
      :isFlowEdited="isFlowEdited"
      :blocklyEnabled="!disableBlockly"
      :isMaintainerEdited="isMaintainerEdited"
      :useDarkTheme="isDarkBorderRequired"
    />
  </section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../../scss/variables';
#flow {
  display: flex;
  flex-direction: column;

  #editor {
    flex: 1 0 100px;
    margin-top: 0;
    overflow-y: hidden; // Don't allow overflowing content to hide the bottom menu. There shouldn't be any!
    form {
      height: 100%;

      .codeEditor {
        height: 100%;
      }
      #blockly {
        height: 100%;
      }
      .changelog {
        height: 100%;
      }
    }
    &.dark {
      background-color: #1e1e1e; // Comes from monaco dark theme
      border-color: #555555; // Comes from fomantic inverted segment
    }
  }
}
</style>

<script>
// @flow
import Vue from 'vue'

import {
  Auth,
  Application,
  Flow,
  Toast,
  Group,
  User,
  Field,
  Subject,
  Typenames,
  Changelog as ChangelogService,
  ErrorCodes,
  conf,
  UnsavedChecker,
  Hub
} from 'services'
import ApplicationMenu from './../ApplicationMenu'
import CodeEditor from './CodeEditor'
import FlowSettings from './FlowSettings'
import FlowExtensions from './FlowExtensions'
import FlowButtonMenu from './FlowButtonMenu'
import Changelog from './Changelog/Changelog'
import Debugger from './Debugger/Debugger'
import FlowHub from './../../Hub/FlowHub'
import _ from 'lodash'
import { required } from 'vuelidate/lib/validators'
import Blocklify from '../../../blockly/Blocklify/Blocklify'
import BlocklyEditor from './BlocklyEditor'
import { ActionTypes, MutationTypes } from 'store'
import { mapMutations, mapGetters } from 'vuex'
import { mapFields, mapMultiRowFields } from 'vuex-map-fields'
import GroupAffiliated from '../../Mixins/GroupAffiliated'

function isTrue(v) {
  return v
}

export default {
  name: 'flow-editor',
  mixins: [UnsavedChecker.mixin, GroupAffiliated],
  components: {
    ApplicationMenu,
    CodeEditor,
    FlowSettings,
    FlowButtonMenu,
    BlocklyEditor,
    Changelog,
    Debugger,
    FlowHub,
    FlowExtensions
  },

  computed: {
    ...mapFields('flowEditor', [
      'isCurrentFlowVersionOutdated',
      'blocklyXml',
      'app',
      'lastEditedBy',
      'codeInEditor'
    ]),
    ...mapGetters('flowEditor', ['isManagedApp', 'isDarkThemeActive']),
    ...mapGetters(['userPreferences', 'userPrefsInitialized']),
    ...mapMultiRowFields('flowEditor', {
      flowExtensions: 'flow.extensions'
    }),

    canDelete() {
      return this.app?.launchWithFlowId !== this.flowId
    },

    isDarkBorderRequired() {
      return (this.editorMode === 'code' || this.editorMode === 'blockly') && this.isDarkThemeActive
    },
    flow() {
      return this.$store.state.flowEditor.flow || {}
    },
    allApps() {
      return this.$store.state.application.items
    },
    allFields() {
      return this.$store.state.field.items
    },
    users() {
      return this.$store.state.user.items
    },
    allFlows() {
      return this.$store.state.flow.items
    },
    fieldsInCode() {
      return this.$store.state.flowEditor.fieldsInCode
    },
    flowsInCode() {
      return this.$store.state.flowEditor.flowsInCode
    },
    isAutosaveDisabled() {
      return !this.userPreferences.autoSave || _.some(this.savedGroups, { disableAutosave: true })
    },
    isCurrentUserAllowedToEditFlow() {
      return (
        conf.allowNonMaintainersToEditFlows ||
        !this.flowUser.name ||
        this.flowUser.name === Auth.getLoggedInUser() ||
        _.isEmpty(this.flow)
      )
    },
    isFlowEditingDisabled() {
      return (
        !this.isCurrentUserAllowedToEditFlow ||
        _.some(this.savedGroups, { disableFlowsEditing: true }) ||
        this.isCurrentFlowVersionOutdated
      )
    },
    applicationFields() {
      return _.filter(this.allFields, { application: this.appId })
    },
    savedGroups() {
      return _.filter(this.groups, g => _.includes(g.flow, this.flowId))
    },
    usedFields() {
      return this.$store.getters['flowEditor/usedFields']()
    },
    blocklyEnabled() {
      // Disabled if there is code and no blocks
      return !(this.codeInEditor.es5Code && !this.blocklyXml)
    },
    isModuleTypeFlow() {
      return this.flow.type === 'MODULE'
    },
    editorMode() {
      let editorMode = this.mode
      if (this.mode === 'auto' && this.flow) {
        if (
          !this.isManagedApp ||
          (this.flow.publication && this.flow.publication.type !== Hub.FLOW_PUBLICATION)
        )
          editorMode = 'settings'
        else editorMode = !this.disableBlockly && this.flow.blocklyXml ? 'blockly' : 'code'
      }
      this.setEditorMode(editorMode)
      return editorMode
    },
    flowUser() {
      return _.find(this.users, { id: this.savedFlowMaintainer }) || {}
    },
    isMaintainerEdited() {
      return this.savedFlowMaintainer !== this.flow.maintainer
    },
    isCodeEdited() {
      const actualEditorCode = this.usesTranspilation
        ? this.codeInEditor.code
        : this.codeInEditor.es5Code
      return this.flowEditorCode !== actualEditorCode
    },
    isBlocklyXmlEdited() {
      return this.flow.blocklyXml !== this.blocklyXml
    },
    usesTranspilation() {
      return this.flow.language && this.flow.language !== Flow.supportedLanguages.ES5
    },
    flowEditorCode() {
      return this.usesTranspilation ? this.flow.editorCode : this.flow.code
    },
    isFlowEdited() {
      return (
        !_.isEqual(this.savedFlow, this.flow) ||
        this.areGroupsEdited ||
        this.isMaintainerEdited ||
        this.isCodeEdited ||
        this.isBlocklyXmlEdited
      )
    },
    areChildrenComponentsValid() {
      return this.$store.getters['flowEditor/areChildrenComponentsValid']()
    },
    savesRequested() {
      return this.$store.state.savesRequested
    }
  },

  props: {
    appId: {
      type: String,
      default: () => ''
    },
    flowId: {
      type: String,
      default: () => ''
    },
    mode: {
      type: String,
      default: () => ''
    },
    modeArg: {
      type: String,
      default: () => ''
    }
  },

  data() {
    return {
      savedFlow: {},
      updatingPaused: false,
      debouncedUpdate: _.debounce(this.updateFlow, 1500),
      disableBlockly: conf.disableBlockly,
      savedFlowMaintainer: '',
      isDebuggerEnabled: !conf.disableDebugger,
      lastParseError: '',
      extensionsValid: true
    }
  },

  validations: {
    flow: {
      name: {
        required
      }
    },
    extensionsValid: {
      isTrue
    }
  },

  methods: {
    ...mapMutations('flowEditor', [
      'updateFlowState',
      'clearStoreState',
      'setEditorMode',
      'setIsCurrentUserAllowedToEditFlow',
      'setIsFlowEditingDisabled'
    ]),

    extensionValidates(valid) {
      this.extensionsValid = valid
    },

    updateIfRequired() {
      const changePending = this.$refs.codeEditor.signalSave() // Inner components can label async changes being processed so they are saved upon completion
      // If a change is pending, we postpone saving until it arrives
      if (this.isItemDirty && !this.updatingPaused && !changePending) this.updateFlow()
    },

    showParseErrorWarning() {
      if (!this.lastParseError) return
      Toast.warning(
        this.$i18n.t('WARN_SAVE_WITH_ERRORS', {
          error: this.lastParseError
        }),
        { text: this.lastParseError }
      )
      this.lastParseError = ''
    },

    updateFlow() {
      if (!this.$v.$invalid && this.areChildrenComponentsValid) {
        if (!this.isCurrentFlowVersionOutdated) {
          // If code changed and blocklyXml didn't, then it's a direct code change
          if (this.blocklyXml && this.isCodeEdited && !this.isBlocklyXmlEdited) {
            // NOTE: Any code change not from blockly causes us to delete the blocks
            if (this.disableBlockly || confirm(this.$i18n.t('BLOCKLY_CHANGE_WARN'))) {
              // Reset blockly xml and proceed with save of code change
              this.blocklyXml = ''
            } else {
              // Roll back the editor change and abort save of code change
              this.updatingPaused = true
              this.codeInEditor = {
                es5Code: this.flow.code,
                code: this.flow.editorCode,
                sourcemap: this.flow.sourcemap
              }
              return Promise.reject('Abort save to save blocks')
            }
          }
          this.showParseErrorWarning()
          return new Promise((resolve, reject) => {
            this.updatingPaused = true
            // Note: We don't update the vuex version of the flow until after the save to the backend for performance reasons
            const saveObject = this.buildSaveObject()
            const updatedFlow = _.extend({}, this.flow, saveObject)
            return Flow.updateFlow(this.flow.id, updatedFlow)
              .then(res => {
                this.debouncedUpdate.cancel()
                this.updateFlowState({ ...updatedFlow, version: res.version })
                this.savedFlowMaintainer = this.flow.maintainer
                return this.saveGroups('flow', this.flow.id)
              })
              .then(() => {
                this.refreshFlowData()
                this.savedFlow = _.cloneDeep(this.flow)
                this.updatingPaused = false
                Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('FLOW', 1) }))
                resolve()
              })
              .catch(err => {
                console.error(err)
                if (err.code === ErrorCodes.ITEM_OUTDATED) {
                  this.isCurrentFlowVersionOutdated = true
                  Toast.error(
                    this.$i18n.t('ERR_OUTDATED_VERSION', {
                      action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('FLOW', 1) }),
                      thing: this.$i18n.tc('FLOW', 1)
                    })
                  )
                } else {
                  Toast.error(
                    this.$i18n.t('ERR', {
                      action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('FLOW', 1) })
                    })
                  )
                }
                this.updatingPaused = false
                reject(err)
              })
          })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('FLOW', 1) }),
              thing: this.$i18n.tc('FLOW', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_INCORRECT_INPUT', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('FLOW', 1) })
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    buildSaveObject() {
      return {
        code: this.codeInEditor.es5Code,
        editorCode: this.codeInEditor.code,
        sourcemap: this.codeInEditor.sourcemap,
        blocklyXml: this.blocklyXml || '',
        usesfields: _.uniq(
          _.concat(
            this.usedFields,
            _.filter(_.map(this.flow.extensions, e => (e.type === 'field' ? e.value : false)))
          )
        ),
        usesFlows: _.uniq(
          _.concat(
            this.flowsInCode,
            _.filter(_.map(this.flow.extensions, e => (e.type === 'flow' ? e.value : false)))
          )
        )
      }
    },

    deleteFlow() {
      return Promise.all(
        this.savedGroups.map(g => Group.removeAffiliate('flow', g.id, this.flow.id))
      )
        .then(() => Flow.deleteFlow(this.flow.id))
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('FLOW', 1) }), {
            remove: 5000
          })

          this.$router.push({
            name: 'app-settings',
            params: { appId: this.appId }
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('FLOW', 1) })
            }),
            { text: this.$i18n.t('TRY_AGAIN') }
          )
          console.error(err)
        })
    },

    copyFlow() {
      if (!confirm(this.$t('COPY_CONFIRM', { thing: this.flow.name }))) return
      let defaultMaintainer = _.find(this.users, { name: Auth.getLoggedInUser() })
      const newFlow = _.extend({}, this.flow, {
        name: `${this.flow.name} (${this.$t('COPIED_BY', {
          name: defaultMaintainer ? defaultMaintainer.name : this.$t('UNKNOWN')
        })})`,
        maintainer: defaultMaintainer ? defaultMaintainer.id : ''
      })
      delete newFlow.id
      return Flow.createFlow(newFlow)
        .then(flowCopy => {
          Toast.success(this.$i18n.t('COPIED', { thing: this.$i18n.tc('FLOW', 1) }), {
            remove: 5000
          })
          this.$router.push({
            name: 'app-flow',
            params: { appId: this.appId, flowId: flowCopy.id, mode: 'settings', modeArg: 'copy' }
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('COPY', { thing: this.$i18n.tc('FLOW', 1) })
            })
          )
          console.error(err)
        })
    },

    codeParsed(code: string, saveRequested, parseError) {
      this.lastParseError = parseError || '' // Store error so we can show it if the user decides to save
      if (
        this.flowEditorCode !== code &&
        !this.updatingPaused &&
        (!parseError || saveRequested) &&
        (!this.isAutosaveDisabled || saveRequested) &&
        !this.isCurrentFlowVersionOutdated
      ) {
        this.updateFlow() // This event is already debounced
      }
    },

    blocklyUpdated(blocklyData) {
      if (blocklyData && blocklyData.xml !== this.blocklyXml) {
        if (blocklyData.code !== this.codeInEditor.es5Code) {
          this.codeInEditor = {
            es5Code: blocklyData.code,
            code: this.usesTranspilation && blocklyData.code,
            sourcemap: ''
          }
        }
        this.blocklyXml = blocklyData.xml
        if (
          !this.updatingPaused &&
          !this.isAutosaveDisabled &&
          !this.isCurrentFlowVersionOutdated
        ) {
          this.debouncedUpdate()
        }
      }
    },

    generateBlocksFromCode() {
      const numCodeLines = this.flow && this.flow.code ? this.flow.code.split('\n').length : 0
      let message = ''
      if (numCodeLines > 400) {
        message = this.$i18n.t('BLOCKLY_GENERATE_LARGE_CODE_MSG')
      } else if (numCodeLines > 200) {
        message = this.$i18n.t('BLOCKLY_GENERATE_MEDIUM_CODE_MSG')
      }

      if (!message || confirm(message)) {
        const xml = Blocklify.jsToBlocks(this.codeInEditor.es5Code)
        this.blocklyUpdated({ xml, code: this.codeInEditor.es5Code })
      }
    },

    onFlowReverted(codeObj, version, blocklyXml) {
      this.codeInEditor = codeObj
      this.blocklyXml = blocklyXml
      this.updateFlow()
    },

    getLastEditedBy() {
      return ChangelogService.getLastEditedBy(this.flow.id, Typenames.FLOW).then(lastEditedBy => {
        this.lastEditedBy = lastEditedBy
      })
    },

    refreshFlowData() {
      if (this.flow && !_.isEmpty(this.flow)) {
        this.updatingPaused = true
        this.resetGroupChanges()
        this.isCurrentFlowVersionOutdated = false
        this.blocklyXml = this.flow.blocklyXml
        this.savedFlowMaintainer = this.flow.maintainer
        this.updatingPaused = false
        this.getLastEditedBy()
      }
    }
  },

  created() {
    Flow.readAllFlows().then(() => {
      Flow.readFlow(this.flowId).then(flow => {
        if (!flow.language) flow.language = Flow.supportedLanguages.ES5
        this.updateFlowState(flow)
      })
    })
    Group.readAllGroups()
    Application.readAllApps()
    Field.readFields()
    User.readAllUsers()
    Subject.readAllSubjects()
  },

  watch: {
    flowId: {
      immediate: true,
      handler(newFlowId) {
        if (newFlowId !== this.flow.id) {
          this.clearStoreState()
          this.updatingPaused = true
          Flow.readFlow(newFlowId).then(flow => {
            //this.updateFlowState({ editorCode: null, ...flow })
            if (!flow.language) flow.language = Flow.supportedLanguages.ES5
            this.updateFlowState(flow)
            this.refreshFlowData()
            this.codeInEditor = {
              es5Code: flow.code,
              code: flow.editorCode,
              sourcemap: flow.sourcemap
            }
            this.updatingPaused = false
          })
        }
      }
    },
    flow: {
      handler(flow, prevFlow) {
        if (
          _.isEmpty(this.savedFlow) ||
          flow.id !== prevFlow.id ||
          flow.version !== prevFlow.version
        ) {
          this.savedFlow = _.cloneDeep(flow)
          this.savedFlowMaintainer = flow.maintainer
        }
      },
      deep: true
    },
    appId: {
      immediate: true,
      handler(newAppId) {
        if (newAppId !== this.app.id) {
          // Clear immediately to prevent temporary child component confusion
          this.app = {}
          Application.readApp(newAppId).then(app => {
            this.app = app
          })
        }
      }
    },
    isFlowEditingDisabled: {
      immediate: true,
      handler() {
        this.setIsFlowEditingDisabled(this.isFlowEditingDisabled)
      }
    },
    isCurrentUserAllowedToEditFlow: {
      immediate: true,
      handler() {
        this.setIsCurrentUserAllowedToEditFlow(this.isCurrentUserAllowedToEditFlow)
      }
    },
    isFlowEdited(newVal, oldVal) {
      if (newVal === oldVal) return
      this.$store.commit(newVal ? MutationTypes.SET_DIRTY : MutationTypes.SET_CLEAN)
    },
    savesRequested() {
      this.updateIfRequired()
    }
  }
}
</script>
